import { useRef, useState } from "react";
import {
  Download,
  ExternalLink,
  Eye,
  FileText,
  Landmark,
  Loader2,
  Maximize2,
  RefreshCw,
  Upload,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { WhyDisabled } from "@/components/ui/disabled-reason";
import { useLibraryLink } from "@/hooks/useLibraryLink";
import { useZoteroWatch } from "@/hooks/useZoteroWatch";
import { cn } from "@/lib/utils";
import type { PaperWithRole } from "@/types/paper";

const RETRIEVING_REASON = "Paused while PDF retrieval runs.";

type PdfRowState = "notFetched" | "fetching" | "noOpenAccess" | "fullText" | "ready";

const NO_OPEN_ACCESS = ["Unavailable", "Paywalled", "Not Available"];

/** Which of the four PDF stages a paper is in (plus full-text-only). A PDF is
 * "ready" when either the user's private upload or the canonical OA blob
 * resolves; falls back to canonical_pdf_status when the backend hasn't
 * returned pdf_available_source (older deploys). */
function pdfRowState(paper: PaperWithRole, fetchRequested = false): PdfRowState {
  const hasPdf =
    paper.pdf_available_source === "user" ||
    paper.pdf_available_source === "canonical" ||
    (paper.pdf_available_source === undefined &&
      paper.canonical_pdf_status === "Success" &&
      !!paper.canonical_pdf_storage_key);
  // A click on Fetch / Try again / Fetch again shows as fetching straight away.
  if (fetchRequested) return "fetching";
  if (hasPdf) return "ready";
  if (paper.canonical_pdf_status === "Fetching") return "fetching";
  if (paper.canonical_pdf_status === "Full Text") return "fullText";
  if (NO_OPEN_ACCESS.includes(paper.canonical_pdf_status)) return "noOpenAccess";
  return "notFetched";
}

const STATUS: Record<PdfRowState, { text: string; dot: string }> = {
  notFetched: { text: "not fetched yet", dot: "bg-muted-foreground" },
  fetching: { text: "looking for an open-access copy…", dot: "bg-blue-400" },
  noOpenAccess: { text: "no open-access copy found", dot: "bg-orange-400" },
  fullText: { text: "full text only, no PDF file", dot: "bg-yellow-400" },
  ready: { text: "ready", dot: "bg-green-500" },
};

const formatTime = (ms: number) =>
  new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

interface Props {
  paper: PaperWithRole;
  isRetrieving?: boolean;
  isUploading?: boolean;
  onViewPdf?: (paperId: string) => void;
  onExpandPdf?: (paperId: string) => void;
  onViewFullText?: (paperId: string) => void;
  onDownloadPdf?: (paperId: string) => void;
  onUploadPdf?: (paperId: string, file: File) => void;
  /** Opens Settings → Integrations; shown in the hint when no library link is set. */
  onSetUpLibraryLink?: () => void;
}

/** The paper-detail PDF row: a status line, then the actions for that stage.
 * Getting a PDF (Fetch ⤓ / Library 🏛 / Upload) and opening one (👁) are
 * separate buttons — the same slot never changes meaning.
 *
 * Local "just clicked" flags reset by remounting: the parent keys this row on
 * paper id + PDF status + retrieval + view mode. */
export function PdfRow({
  paper,
  isRetrieving = false,
  isUploading = false,
  onViewPdf,
  onExpandPdf,
  onViewFullText,
  onDownloadPdf,
  onUploadPdf,
  onSetUpLibraryLink,
}: Props) {
  const [fetchRequested, setFetchRequested] = useState(false);
  const [opening, setOpening] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const library = useLibraryLink(paper.doi);

  const state = pdfRowState(paper, fetchRequested);
  const watch = useZoteroWatch(paper.id, state === "ready");
  const waiting = watch.state === "waiting";
  const status = waiting
    ? "waiting for Zotero…"
    : state === "ready"
      ? `ready (${paper.pdf_available_source === "user" ? "your upload" : "open access"})`
      : STATUS[state].text;

  const fetchPdf = () => {
    setFetchRequested(true);
    onDownloadPdf?.(paper.id);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUploadPdf) onUploadPdf(paper.id, file);
    // Reset so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const retrievingWhy = isRetrieving ? RETRIEVING_REASON : null;
  const fetchButton = onDownloadPdf && (
    <WhyDisabled reason={retrievingWhy}>
      <Button size="xs" onClick={fetchPdf} disabled={isRetrieving} className="disabled:pointer-events-none">
        <Download /> Fetch open-access PDF
      </Button>
    </WhyDisabled>
  );
  const againButton = (label: string) =>
    onDownloadPdf && (
      <WhyDisabled reason={retrievingWhy}>
        <Button variant="ghost" size="xs" onClick={fetchPdf} disabled={isRetrieving} className="disabled:pointer-events-none">
          <RefreshCw /> {label}
        </Button>
      </WhyDisabled>
    );

  const libraryButton = (primary: boolean) =>
    library.href && (
      <Button asChild size="xs" variant={primary ? "default" : "outline"}>
        <a
          href={library.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            library.notifyZotero();
            if (library.zoteroConnected) watch.start();
          }}
        >
          <Landmark />
          {library.hasPattern
            ? primary ? "Get it through your library" : "Library"
            : primary ? "Get it from the publisher" : "Publisher"}
          <ExternalLink />
        </a>
      </Button>
    );

  const uploadButton = onUploadPdf && (
    <>
      <Button variant="outline" size="xs" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
        {isUploading ? <Loader2 className="animate-spin" /> : <Upload />}
        {isUploading ? "Uploading…" : "Upload PDF"}
      </Button>
      <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />
    </>
  );

  let buttons: React.ReactNode;
  switch (state) {
    case "notFetched":
      // Open access leads (automatic, free); without a fetch handler the
      // library link is the only way in, so it leads instead.
      buttons = (
        <>
          {fetchButton}
          {libraryButton(!onDownloadPdf)}
          {uploadButton}
        </>
      );
      break;
    case "fetching":
      buttons = (
        <>
          <Button size="xs" disabled>
            <Loader2 className="animate-spin" /> Fetching…
          </Button>
          {libraryButton(false)}
          {uploadButton}
        </>
      );
      break;
    case "noOpenAccess":
      buttons = (
        <>
          {libraryButton(true)}
          {uploadButton}
          {againButton("Try again")}
        </>
      );
      break;
    case "fullText":
      buttons = (
        <>
          {onViewFullText && (
            <Button size="xs" onClick={() => { setOpening(true); onViewFullText(paper.id); }} disabled={opening}>
              {opening ? <Loader2 className="animate-spin" /> : <FileText />} Open full text
            </Button>
          )}
          {libraryButton(false)}
          {uploadButton}
          {againButton("Fetch again")}
        </>
      );
      break;
    case "ready":
      buttons = (
        <>
          {onViewPdf && (
            <Button size="xs" onClick={() => { setOpening(true); onViewPdf(paper.id); }} disabled={opening}>
              {opening ? <Loader2 className="animate-spin" /> : <Eye />} Open PDF
            </Button>
          )}
          {onExpandPdf && (
            <Button variant="outline" size="xs" onClick={() => { setOpening(true); onExpandPdf(paper.id); }} disabled={opening}>
              <Maximize2 /> Expand
            </Button>
          )}
          {paper.pdf_available_source !== "user" && againButton("Fetch again")}
        </>
      );
      break;
  }

  return (
    <section aria-label="PDF" className="flex flex-col gap-1.5 rounded-md border px-2 py-1.5">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span
          aria-hidden
          className={cn("inline-block size-1.5 rounded-full", waiting ? "animate-pulse bg-blue-400" : STATUS[state].dot)}
        />
        PDF · {status}
      </p>
      <div className="flex flex-wrap items-center gap-1">{buttons}</div>
      {watch.state !== "off" && (
        <p className="text-[11px] text-muted-foreground" role="status">
          {watch.manual.status === "checking"
            ? "Checking Zotero… "
            : watch.manual.status === "checked" && waiting
              ? `Checked Zotero at ${formatTime(watch.manual.at)}, nothing yet. `
              : waiting
                ? "Save it with the Zotero Connector and it attaches here, usually within a minute. "
                : "Nothing from Zotero yet. Check that the Connector saved the PDF, not just the reference. "}
          {watch.manual.status !== "checking" && (
            <>
              <button type="button" onClick={watch.checkNow} className="text-primary underline">
                {waiting ? "Check now" : "Check again"}
              </button>
              {" · "}
            </>
          )}
          <button type="button" onClick={watch.stop} className="text-primary underline">
            {waiting ? "Stop" : "Dismiss"}
          </button>
        </p>
      )}
      {state === "noOpenAccess" && library.href && watch.state === "off" && (
        <p className="text-[11px] text-muted-foreground">
          {library.hasPattern ? (
            <>
              Opens your library’s link for this DOI.{" "}
              {library.zoteroConnected
                ? "Zotero is connected, so the PDF attaches here when Zotero saves it."
                : onUploadPdf
                  ? "Download the PDF there, then use Upload PDF here."
                  : null}
            </>
          ) : (
            <>
              Opens the publisher’s page via doi.org.
              {onSetUpLibraryLink && (
                <>
                  {" "}
                  <button type="button" onClick={onSetUpLibraryLink} className="text-primary underline">
                    Set up your library link
                  </button>{" "}
                  to go through your library instead.
                </>
              )}
            </>
          )}
        </p>
      )}
    </section>
  );
}
